import { useState, useRef, useEffect, useMemo } from "react";

interface MonthPickerProps {
  value: string;
  onChange: (value: string) => void;
  minDate?: string;
  maxDate?: string | null;
  placeholder?: string;
  className?: string;
  hasError?: boolean;
  disabled?: boolean;
  id?: string;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const MONTHS_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function toFirstOfMonth(year: number, month: number): string {
  const m = String(month + 1).padStart(2, "0");
  return `${year}-${m}-01`;
}

function parseDate(dateStr: string): { year: number; month: number } | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return { year: d.getFullYear(), month: d.getMonth() };
}

export const MonthPicker: React.FC<MonthPickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = "Select end month",
  className = "",
  hasError = false,
  disabled = false,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => parseDate(value), [value]);
  const min = useMemo(() => parseDate(minDate || ""), [minDate]);
  const max = useMemo(() => (maxDate ? parseDate(maxDate) : null), [maxDate]);

  const [viewYear, setViewYear] = useState(() => {
    if (selected) return selected.year;
    if (min) return min.year;
    return new Date().getFullYear();
  });

  useEffect(() => {
    if (isOpen) {
      if (selected) setViewYear(selected.year);
      else if (min) setViewYear(min.year);
    }
  }, [isOpen, selected, min]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isMonthDisabled = (year: number, month: number): boolean => {
    const dateVal = toFirstOfMonth(year, month);
    if (min && dateVal < toFirstOfMonth(min.year, min.month)) return true;
    if (max && dateVal > toFirstOfMonth(max.year, max.month)) return true;
    return false;
  };

  const isMonthSelected = (year: number, month: number): boolean => {
    return selected !== null && selected.year === year && selected.month === month;
  };

  const canGoPrev = (): boolean => {
    if (!min) return true;
    return viewYear > min.year;
  };

  const canGoNext = (): boolean => {
    if (!max) return true;
    return viewYear < max.year;
  };

  const handleSelect = (month: number) => {
    onChange(toFirstOfMonth(viewYear, month));
    setIsOpen(false);
  };

  const displayValue = useMemo(() => {
    if (!selected) return "";
    const day = "01";
    const m = String(selected.month + 1).padStart(2, "0");
    return `${day}.${m}.${selected.year}`;
  }, [selected]);

  const displayLabel = useMemo(() => {
    if (!selected) return "";
    return `${MONTHS_FULL[selected.month]} ${selected.year}`;
  }, [selected]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full border p-3 text-left bg-white dark:bg-primary-800 flex justify-between items-center transition-colors ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        } ${
          hasError
            ? "border-red-400"
            : isOpen
              ? "border-primary-500 dark:border-primary-400"
              : "border-gray-200 dark:border-primary-700 hover:border-primary-400 dark:hover:border-primary-500"
        }`}
      >
        <div className="flex flex-col">
          {selected ? (
            <>
              <span className="text-sm font-medium text-primary-600 dark:text-white">{displayValue}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{displayLabel}</span>
            </>
          ) : (
            <span className="text-sm text-gray-400 dark:text-gray-500">{placeholder}</span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-20 mt-1 w-72 bg-white dark:bg-primary-800 border border-gray-200 dark:border-primary-700 rounded-lg shadow-xl animate-in fade-in slide-in-from-top-1 duration-150">
          {/* Year navigation */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-primary-700">
            <button
              type="button"
              onClick={() => canGoPrev() && setViewYear((y) => y - 1)}
              disabled={!canGoPrev()}
              className={`p-1 rounded-md transition-colors ${
                canGoPrev()
                  ? "hover:bg-gray-100 dark:hover:bg-primary-700 text-primary-600 dark:text-white"
                  : "text-gray-300 dark:text-primary-600 cursor-not-allowed"
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-primary-700 dark:text-white select-none">{viewYear}</span>
            <button
              type="button"
              onClick={() => canGoNext() && setViewYear((y) => y + 1)}
              disabled={!canGoNext()}
              className={`p-1 rounded-md transition-colors ${
                canGoNext()
                  ? "hover:bg-gray-100 dark:hover:bg-primary-700 text-primary-600 dark:text-white"
                  : "text-gray-300 dark:text-primary-600 cursor-not-allowed"
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-3 gap-1 p-3">
            {MONTHS.map((name, idx) => {
              const disabled = isMonthDisabled(viewYear, idx);
              const active = isMonthSelected(viewYear, idx);
              return (
                <button
                  key={name}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSelect(idx)}
                  className={`py-2 px-1 rounded-md text-sm font-medium transition-all ${
                    active
                      ? "bg-primary-600 text-white shadow-sm"
                      : disabled
                        ? "text-gray-300 dark:text-primary-600 cursor-not-allowed"
                        : "text-primary-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-700"
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
